import React from 'react'

import type { DashboardStatsData } from '@/types/dashboard_stats.types'

import { KpiCard } from './KpiCard'

interface KpiGridProps {
  resumenEstados: DashboardStatsData['resumen_estados']
  produccionReciente: DashboardStatsData['produccion_reciente']
}

const FASE_LABELS: Record<string, string> = {
  'current-level': 'Nivel actual',
  'gap-analysis': 'Análisis de brechas',
  recommendations: 'Recomendaciones',
  planning: 'Planificación'
}

export const KpiGrid: React.FC<KpiGridProps> = ({ resumenEstados, produccionReciente }) => {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
      <KpiCard
        label="En curso"
        value={resumenEstados['ongoing'] ?? 0}
        href="/assessments?workflow_estado=ongoing"
        colorClass="text-blue-700"
        bgClass="bg-blue-50"
        borderClass="border-blue-200"
      />
      <KpiCard
        label="En borrador"
        value={resumenEstados['draft'] ?? 0}
        href="/assessments?workflow_estado=draft"
        colorClass="text-amber-700"
        bgClass="bg-amber-50"
        borderClass="border-amber-200"
      />
      <KpiCard
        label="Completados"
        value={resumenEstados['completed'] ?? 0}
        href="/assessments?workflow_estado=completed"
        colorClass="text-emerald-700"
        bgClass="bg-emerald-50"
        borderClass="border-emerald-200"
      />
      <KpiCard
        label="Finalizados"
        value={resumenEstados['finished'] ?? 0}
        href="/assessments?workflow_estado=finished"
        colorClass="text-gray-700"
        bgClass="bg-gray-50"
        borderClass="border-gray-200"
      />
      <KpiCard
        label="Total histórico"
        value={resumenEstados['total'] ?? 0}
        sublabel={`+${produccionReciente.total} últimos ${produccionReciente.periodo_dias}d`}
        colorClass="text-primary-700"
        bgClass="bg-primary-50"
        borderClass="border-primary-200"
      />
    </div>
  )
}

interface FaseDistribucionProps {
  desgloseFases: DashboardStatsData['desglose_fases_ongoing']
  totalOngoing: number
}

export const FaseDistribucion: React.FC<FaseDistribucionProps> = ({
  desgloseFases,
  totalOngoing
}) => {
  if (totalOngoing === 0) return null

  const fases = Object.entries(desgloseFases).map(([fase, count]) => ({
    fase,
    count,
    label: FASE_LABELS[fase] ?? fase,
    pct: totalOngoing > 0 ? Math.round((count / totalOngoing) * 100) : 0
  }))

  const COLORS: Record<string, string> = {
    'current-level': 'bg-blue-500',
    'gap-analysis': 'bg-violet-500',
    recommendations: 'bg-amber-500',
    planning: 'bg-emerald-500'
  }

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-700 mb-3">Assessments en curso por fase</h3>
      <div className="space-y-2">
        {fases.map(({ fase, count, label, pct }) => (
          <div key={fase} className="flex items-center gap-3">
            <span className="text-xs text-gray-500 w-36 shrink-0">{label}</span>
            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-300 ${COLORS[fase] ?? 'bg-gray-400'}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="text-xs font-medium text-gray-700 w-6 text-right">{count}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
